import logging from '../logging';
import { BufferAttribute, BufferGeometry, Line, LineBasicMaterial, Mesh, MeshBasicMaterial, Object3D, PerspectiveCamera } from 'three';
import { GridLayout } from '../imw/grid-layout';
import { ONE_DEGREE_IN_RADIANS } from '../math/constants';
import { computeFrameSize } from './compute-frame-size';
import { generateBufferGeometry } from '../imw/generate-buffer-geometry';
import { CameraConfig } from '../views/views-config';

const log = logging('view3d');

const
    // add an extra 1 degree to the outer edge of the mask
    CLIPPING_MARGIN = 1,
    // add an extra 1.5 degrees to the vFov so we see the mask on all edges
    MASK_MARGIN = 1.5;

/**
 * Adjust the field of view of the given camera so that the 
 */
const fitfov = (camera: PerspectiveCamera, vFov: number, aspect: number) => {
    // For the letterboxed case, the camera aspect is less than the inset
    // aspect, so we need to increase the camera's vertical field of view to
    // create the letterbox bands.
    if(camera.aspect < aspect) {
        // We convert the inset's vertical field of view angle into a measure
        // on the picture plane (at unit distance), transform the measure into
        // a horizontal measure of the inset using the inset's aspect ratio,
        // then get the desired vertical measure for the camera using the
        // camera's aspect ratio. Finally, the adjusted vertical measure from
        // the camera's picture plane is converted back to an angle and
        // assigned to the camera's fov property.
        const
            vFovRad = vFov * ONE_DEGREE_IN_RADIANS,
            halfVerticalFrameSize = Math.tan(vFovRad/2),
            halfHorizontalFrameSize = halfVerticalFrameSize * aspect,
            halfVerticalCameraSize = halfHorizontalFrameSize / camera.aspect,
            newVFovRad = 2*Math.atan(halfVerticalCameraSize);
        camera.fov = newVFovRad / ONE_DEGREE_IN_RADIANS;
    }
    // Otherwise this is the pillarboxed case, and we just copy the vfov
    else {
        camera.fov = vFov;
    }
    camera.updateProjectionMatrix();
};

/**
 * This places a mask in front of the camera to visualize a standard size of
 * camera field-of-view and aspect ratio. For example, our legacy camera was
 * always a 16:9 camera with a 65 degree vertical field of view.
 */
export class CameraFramingHelper extends Object3D {
    camera: PerspectiveCamera;
    mask: Mesh;
    border: Line;
    borderPositions = new BufferAttribute( new Float32Array(15), 3);

    vFov: number;
    aspect: number;

    constructor(camera: PerspectiveCamera, vFov: number, aspect: number) {
        super();
        this.camera = camera;
        this.vFov = vFov;
        this.aspect = aspect;

        this.mask = new Mesh(
            new BufferGeometry(),
            new MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0.66,
                depthWrite: false,
                depthTest: false,
            }));
        this.mask.name = 'CameraFramingHelper Mask';
        this.add(this.mask);

        this.border = new Line(
            new BufferGeometry(),
            new LineBasicMaterial({
                color: 0x000000,
                // This isn't really transparent, we just want it to render
                // after the mask.
                transparent: true,
            }));
        this.border.name = 'CameraFramingHelper Mask Border';
        this.border.geometry.setAttribute('position', this.borderPositions);
        this.add(this.border);

        camera.add(this);
        this.update();
    }

    update() {
        // Set the fov of the camera to enclose the masking region
        fitfov(this.camera, this.vFov + MASK_MARGIN, this.aspect);

        // Compute the size of the outer frame at the near clipping plane
        const
            distance = this.camera.near + CLIPPING_MARGIN,
            [outerWidth, outerHeight] = 
                computeFrameSize(
                    this.camera.fov, // + 1, // add one degree for some margin
                    this.camera.aspect,
                    distance),
            [innerWidth, innerHeight] =
                computeFrameSize(this.vFov, this.aspect, distance),
            layout = new GridLayout(),
            innerLeft = -innerWidth/2,
            innerRight = innerLeft + innerWidth,
            innerBottom = -innerHeight/2,
            innerTop = innerBottom + innerHeight;

        layout.rect(-outerWidth/2, -outerHeight/2, outerWidth, outerHeight);
        layout.void(innerLeft, innerBottom, innerWidth, innerHeight);
        this.mask.geometry = generateBufferGeometry(layout);
        this.mask.position.set(0, 0, -distance);

        // Points A, B, C, and D form a rectangle, starting from the bottom
        // left corner going counter-clockwise.
        const e = this.borderPositions.array;
        e[0]  = innerLeft;  e[1]  = innerBottom; e[2]  = 0;     // A
        e[3]  = innerRight; e[4]  = innerBottom; e[5]  = 0;     // B
        e[6]  = innerRight; e[7]  = innerTop;    e[8]  = 0;     // C
        e[9]  = innerLeft;  e[10] = innerTop;    e[11] = 0;     // D
        e[12] = innerLeft;  e[13] = innerBottom; e[14] = 0;     // A

        this.borderPositions.needsUpdate = true;
        this.border.position.set(0, 0, -distance);
    };

    exportCamera(): PerspectiveCamera {
        const result = new PerspectiveCamera();
        result.position.copy(this.camera.position);
        result.rotation.copy(this.camera.rotation);
        result.fov = this.vFov;
        return result;
    }
}
export default CameraFramingHelper;
